import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useListManager } from '../../hooks/useListManager';
import type { Identifiable } from '../../types/types';
import { SortableItemWrapper } from '../DataPanels/SortableItemWrapper';

interface EditableListManagerProps<T extends Identifiable> {
  items: T[];
  onChange: (newItems: T[] | ((prevItems: T[]) => T[])) => void;
  defaultNewItem: () => Omit<T, 'id'>; // Omit 'id' from the default item
  renderItemFields: (
    item: T,
    index: number,
    handleItemChange: (index: number, updatedFields: Partial<T>) => void
  ) => React.ReactNode;
  addText: string;
  listStyle?: React.CSSProperties;
}

const EditableListManager = <T extends Identifiable>({
  items,
  onChange,
  defaultNewItem,
  renderItemFields,
  addText,
  listStyle,
}: EditableListManagerProps<T>) => {
  const { addItem, updateItem, deleteItem } = useListManager<T>(onChange);
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      onChange((currentItems) => {
        const oldIndex = (currentItems ?? []).findIndex(item => item.id === Number(active.id));
        const newIndex = (currentItems ?? []).findIndex(item => item.id === Number(over.id));
        return arrayMove(currentItems, oldIndex, newIndex);
      });
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={(items ?? []).map(item => String(item.id))} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...listStyle }}>
          {(items ?? []).map((item, index) => (
            <SortableItemWrapper key={item.id} item={item}>
              {({ attributes, listeners }) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div {...attributes} {...listeners} style={{ cursor: 'grab', touchAction: 'none' }}>☰</div>
                  <div style={{ flexGrow: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {renderItemFields(item, index, updateItem)}
                  </div>
                  <Button icon={<DeleteOutlined />} onClick={() => deleteItem(index)} size="small" danger />
                </div>
              )}
            </SortableItemWrapper>
          ))}
        </div>
      </SortableContext>
      <Button
        type="dashed"
        onClick={() => addItem(() => ({ ...defaultNewItem(), id: -Date.now() } as T))}
        icon={<PlusOutlined />}
        style={{ marginTop: '1rem', width: '100%' }}
      >
        {addText}
      </Button>
    </DndContext>
  );
};

export default EditableListManager;